/**
 * Invitation à installer BTPal en application (PWA), présentée une fois après
 * connexion. Sur Android/desktop, le bouton déclenche le dialogue natif. Sur
 * iOS, où ce dialogue n'existe pas, on affiche la manip Safari.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Smartphone, Download, Share, Plus } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui';
import { usePwaInstall } from '@/hooks/usePwaInstall';

const DISMISSED_KEY = 'btpal-pwa-install-dismissed';

function wasDismissed(): boolean {
    try {
        return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
        return false;
    }
}

function rememberDismissed(): void {
    try {
        localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
        // Mode privé / stockage indisponible : on n'insiste pas, sans casser.
    }
}

export function InstallAppPrompt(): ReactNode {
    const { canInstall, iosInstallable, isInstalled, install } = usePwaInstall();
    const [open, setOpen] = useState(false);

    const eligible = (canInstall || iosInstallable) && !isInstalled && !wasDismissed();

    // Ouverture différée : laisse le dashboard s'afficher et donne à
    // `beforeinstallprompt` le temps d'arriver sur Android.
    useEffect(() => {
        if (!eligible) return;
        const timer = setTimeout(() => setOpen(true), 1200);
        return () => clearTimeout(timer);
    }, [eligible]);

    const dismiss = (): void => {
        rememberDismissed();
        setOpen(false);
    };

    const handleInstall = async (): Promise<void> => {
        const outcome = await install();
        // Accepté : `appinstalled` referme via l'effet ci-dessus. Refusé ou
        // indisponible : on note la fermeture pour ne plus relancer à chaque login.
        if (outcome !== 'accepted') rememberDismissed();
        setOpen(false);
    };

    // Se referme aussi si l'app est installée entre-temps (isInstalled -> true).
    const visible = open && !isInstalled;
    if (!visible) return null;

    return (
        <Dialog open={visible} onOpenChange={(next) => { if (!next) dismiss(); }}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0F2744]/10 text-[#0F2744] dark:bg-[#2563EB]/15 dark:text-[#60A5FA]">
                            <Smartphone className="h-6 w-6" />
                        </span>
                        <DialogTitle className="text-[#1E293B] dark:text-[#F1F5F9]">
                            Installer BTPal
                        </DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">
                        Ajoutez BTPal à votre écran d'accueil pour l'ouvrir en un tap, en
                        plein écran, comme une vraie application.
                    </DialogDescription>
                </DialogHeader>

                {iosInstallable ? (
                    <ol className="space-y-3 py-2 text-sm text-[#334155] dark:text-[#CBD5E1]">
                        <li className="flex items-center gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] text-xs font-semibold text-[#0F2744] dark:bg-[#334155] dark:text-[#F1F5F9]">1</span>
                            <span className="flex items-center gap-1.5">
                                Touchez <Share className="inline h-4 w-4 text-[#0F2744] dark:text-[#60A5FA]" /> <strong>Partager</strong> dans la barre Safari.
                            </span>
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] text-xs font-semibold text-[#0F2744] dark:bg-[#334155] dark:text-[#F1F5F9]">2</span>
                            <span className="flex items-center gap-1.5">
                                Choisissez <Plus className="inline h-4 w-4 text-[#0F2744] dark:text-[#60A5FA]" /> <strong>Sur l'écran d'accueil</strong>.
                            </span>
                        </li>
                        <li className="flex items-center gap-3">
                            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] text-xs font-semibold text-[#0F2744] dark:bg-[#334155] dark:text-[#F1F5F9]">3</span>
                            <span>Validez avec <strong>Ajouter</strong>.</span>
                        </li>
                    </ol>
                ) : null}

                <DialogFooter>
                    <Button variant="ghost" onClick={dismiss}>
                        Plus tard
                    </Button>
                    {canInstall ? (
                        <Button variant="default" onClick={handleInstall}>
                            <Download className="h-4 w-4" />
                            Installer l'application
                        </Button>
                    ) : (
                        <Button variant="default" onClick={dismiss}>
                            J'ai compris
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default InstallAppPrompt;
